import { useState } from "react";
import { CheckCircle2, ChevronDown, ChevronRight, Search } from "lucide-react";

import type { Movimiento } from "../api/tipos.ts";
import { usarDecidir, usarLibro, type Decision } from "../ganchos/datos.ts";
import { usarSesion } from "../sesion/ContextoSesion.tsx";
import { PERMISO } from "../sesion/permisos.ts";
import { haceRato, pesos, plural, soloHora, fechaCorta } from "../formato.ts";
import { ErrorConsulta, EsqueletoLista } from "../componentes/Errores.tsx";
import { Encuentro } from "../componentes/Encuentro.tsx";
import { AvisoHecho } from "../componentes/AvisoHecho.tsx";
import { cargaDecision, mensajeDeDecision } from "../componentes/decision-modelo.ts";
import { Seccion, Tarjeta, Vacio, cx } from "../componentes/ui.tsx";
import { listo, pagosDeCaja } from "./caja-modelo.ts";

/**
 * La caja (SPEC-029 §12).
 *
 * Para el cajero, con el cliente delante. Responde una sola pregunta —«¿ya
 * llegó?»— y por eso no tiene nada más: ni cartera, ni métricas, ni alertas.
 * Arriba, un campo de monto grande; debajo, lo que entró hoy, que se actualiza
 * solo cada 10 segundos. Un pago que falta identificar se resuelve aquí mismo
 * con el buscador de clientes; lo demás, al dueño en la bandeja.
 */
export function Caja() {
  const { data: libro, isPending, error, refetch, dataUpdatedAt } = usarLibro(true);
  const decidir = usarDecidir();
  const { puedo } = usarSesion();
  const [q, setQ] = useState("");
  const [abierto, setAbierto] = useState<string | null>(null);
  const [hecho, setHecho] = useState<{ texto: string; clave?: string; id: number } | null>(null);

  if (error) return <ErrorConsulta error={error} alReintentar={() => void refetch()} />;

  const pagos = pagosDeCaja(libro?.movimientos ?? [], q);
  const alDecidir = (d: Decision, m: Movimiento) =>
    decidir.mutate(d, {
      onSuccess: () => {
        setAbierto(null);
        setHecho({ texto: mensajeDeDecision(d, m.contraparte), clave: d.accion === "ANULAR" ? undefined : d.clave, id: Date.now() });
      },
    });

  return (
    <Seccion
      titulo="Caja"
      descripcion={`Lo que entró ${q.trim() ? "y coincide con lo que buscas" : "hoy"}. Se actualiza solo${dataUpdatedAt ? ` · ${haceRato(new Date(dataUpdatedAt).toISOString())}` : ""}.`}
    >
      {hecho && (
        <AvisoHecho
          key={hecho.id}
          texto={hecho.texto}
          alDeshacer={
            hecho.clave
              ? () =>
                  decidir.mutate(cargaDecision({ clave: hecho.clave!, accion: "ANULAR" }), {
                    onSuccess: () => setHecho({ texto: "Deshecho. El pago volvió a como estaba.", id: Date.now() }),
                  })
              : undefined
          }
          deshaciendo={decidir.isPending && decidir.variables?.accion === "ANULAR"}
        />
      )}

      <label className="relative block">
        <span className="sr-only">Buscar un pago por monto o nombre</span>
        <Search className="pointer-events-none absolute top-1/2 left-4 size-5 -translate-y-1/2 text-tenue-2" aria-hidden />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          inputMode="search"
          placeholder="¿Cuánto pagó? 41000 · o su nombre"
          className="cifra h-14 w-full rounded-2xl border border-borde bg-superficie pr-4 pl-12 text-xl placeholder:text-base placeholder:text-tenue-2 focus:border-marca focus:ring-4 focus:ring-marca/12 focus:outline-none"
        />
      </label>

      {isPending ? (
        <EsqueletoLista filas={4} />
      ) : pagos.length === 0 ? (
        <Tarjeta>
          <Vacio icono={Search} titulo={q.trim() ? "No ha llegado ningún pago así" : "Todavía no entra nada hoy"}>
            {q.trim()
              ? "Si el cliente acaba de pagar, espera unos segundos: esta lista se actualiza sola."
              : "Cuando el banco avise de un pago, aparece aquí en segundos."}
          </Vacio>
        </Tarjeta>
      ) : (
        <>
          <p className="text-xs text-tenue">
            {pagos.length} {plural(pagos.length, "pago", "pagos")}
          </p>
          <ul className="tarjeta overflow-visible">
            {pagos.map((m) => {
              const ok = listo(m);
              const hora = soloHora(m.fecha);
              const esAbierto = abierto === m.clave;
              return (
                <li key={m.clave} className="border-t border-borde first:border-t-0">
                  <button
                    type="button"
                    onClick={() => setAbierto(esAbierto ? null : m.clave)}
                    aria-expanded={esAbierto}
                    className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-left hover:bg-superficie-2/50"
                  >
                    <span className="w-20 shrink-0 text-xs text-tenue-2">
                      {q.trim() ? `${fechaCorta(m.fecha)} ` : ""}
                      {hora || "—"}
                    </span>
                    <span className="cifra w-32 shrink-0 text-lg font-semibold text-tinta">{pesos(m.montoCentavos)}</span>
                    <span className="min-w-0 flex-1 truncate text-sm text-tinta">
                      {m.contraparte || "el banco no dice quién"}
                      {ok && m.cliente && m.cliente !== m.contraparte ? (
                        <span className="text-tenue"> · {m.cliente}</span>
                      ) : null}
                    </span>
                    <span
                      className={cx(
                        "inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium",
                        ok ? "bg-exito-suave text-exito-tinta" : "bg-aviso-suave text-aviso-tinta",
                      )}
                    >
                      {ok && <CheckCircle2 className="size-3.5" aria-hidden />}
                      {ok ? "Listo" : m.estado === "POR_CONFIRMAR" ? "Falta confirmar" : "Falta decir de quién es"}
                    </span>
                    {esAbierto ? (
                      <ChevronDown className="size-4 text-tenue" aria-hidden />
                    ) : (
                      <ChevronRight className="size-4 text-tenue" aria-hidden />
                    )}
                  </button>
                  {esAbierto && (
                    <div className="border-t border-borde bg-superficie-2/50 p-3">
                      <Encuentro
                        plana
                        movimiento={m}
                        cartera={libro?.facturas ?? []}
                        decidiendo={decidir.isPending}
                        alDecidir={puedo(PERMISO.DECIDIR) ? (d) => alDecidir(d, m) : () => undefined}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Seccion>
  );
}
